import React from 'react';
import { VblAnalysisResult } from '@/types/vbl';
import { VblProbabilityPlot } from './VblProbabilityPlot';
import { VblCDFPlot } from './VblCDFPlot';
import { ColorByOption, MarkerByOption, SizeByOption } from '@/components/analysis/PlotCustomization';

interface DualVblPlotViewProps {
  analysis: VblAnalysisResult | null;
  isLoading?: boolean;
  showConfidenceIntervals?: boolean;
  confidenceLevel?: number;
  showCensoredData?: boolean;
  censoredData?: Array<{ x: number; y: number; time: number; probability: number; caseId?: string }>;
  colorBy?: ColorByOption;
  markerBy?: MarkerByOption;
  sizeBy?: SizeByOption;
  showLegend?: boolean;
}

/**
 * Side-by-side view of both VBL probability and CDF plots
 * Matches DualPlotView structure exactly for consistency
 */
const DualVblPlotView: React.FC<DualVblPlotViewProps> = ({
  analysis,
  isLoading = false,
  showConfidenceIntervals = true,
  confidenceLevel = 95,
  showCensoredData = false,
  censoredData,
  colorBy = 'none',
  markerBy = 'none',
  sizeBy = 'uniform',
  showLegend = false,
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6 h-[500px] flex items-center justify-center">
          <div className="text-gray-500">Loading VBL probability plot...</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 h-[500px] flex items-center justify-center">
          <div className="text-gray-500">Loading VBL CDF plot...</div>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="grid grid-cols-1 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6 h-[500px] flex items-center justify-center">
          <div className="text-gray-500">No Analysis available. Please check applied filters and selected records</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 mb-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Probability Plot */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-700">
              VBL Weibull Probability Plot
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Linearized view for assessing fit quality
            </p>
          </div>
          <div className="pt-2 px-6 pb-4" style={{ height: '480px' }}>
            <VblProbabilityPlot
              analysis={analysis}
              showConfidenceIntervals={showConfidenceIntervals}
              confidenceLevel={confidenceLevel}
              showCensoredData={showCensoredData}
              censoredData={censoredData}
              height={440}
              colorBy={colorBy}
              markerBy={markerBy}
              sizeBy={sizeBy}
              showLegend={showLegend}
            />
          </div>
        </div>

        {/* CDF Plot */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-700">
              VBL Cumulative Distribution Function
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Probability of failure over time
            </p>
          </div>
          <div className="pt-2 px-6 pb-4" style={{ height: '480px' }}>
            <VblCDFPlot
              analysis={analysis}
              showConfidenceIntervals={showConfidenceIntervals}
              confidenceLevel={confidenceLevel}
              showCensoredData={showCensoredData}
              censoredData={censoredData}
              height={440}
              colorBy={colorBy}
              markerBy={markerBy}
              sizeBy={sizeBy}
              showLegend={showLegend}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DualVblPlotView;
